import React, { useCallback, useState } from 'react';
import { Box, Button, Chip, Snackbar, Typography } from '@mui/material';
import { AutoAwesome, ContentCopy, EventRepeat } from '@mui/icons-material';
import { SectionCard, askInkyOrCopy, describeInkyOutcome } from '../pagekit';

/** DOM id of the card (the email's "Plan the follow-up" link scrolls to it and focuses it). */
export const FOLLOW_UP_ANCHOR = 'follow-up';

/**
 * Plain-text version of a follow-up, for the clipboard.
 * @param {object} followUp - Normalized follow-up.
 * @param {string} meetingName - Meeting name.
 * @returns {string} Text.
 */
export function followUpText(followUp, meetingName) {
  return [
    `Follow-up to "${meetingName}": ${followUp.topic}`,
    followUp.reason ? `Why: ${followUp.reason}` : '',
    followUp.suggestedTiming ? `When: ${followUp.suggestedTiming}` : '',
    followUp.suggestedAttendees.length ? `Who: ${followUp.suggestedAttendees.join(', ')}` : '',
  ].filter(Boolean).join('\n');
}

/**
 * "We noticed you may need a follow-up": topic, reason, timing, attendees and the quote from the
 * transcript that suggested it, with "Plan it with Inky" and "Copy".
 * @param {object} props - Props.
 * @param {object} props.followUp - Normalized follow-up (see autopilotModel.normalizeFollowUp).
 * @param {object} props.session - Session ({id, name}).
 * @param {object|null} [props.project] - Project ({id, name}).
 * @returns {JSX.Element} Card.
 */
export default function FollowUpCard({ followUp, session, project = null }) {
  const [toast, setToast] = useState('');
  const meetingName = session?.name || 'this meeting';

  const handlePlan = useCallback(async () => {
    const outcome = await askInkyOrCopy(
      `Help me plan a follow-up meeting to "${meetingName}" about ${followUp.topic}. ` +
      'Draft a short agenda and an invite message I can send. Use the meeting\'s summary and action items.' +
      (followUp.suggestedAttendees.length ? ` Invite: ${followUp.suggestedAttendees.join(', ')}.` : '') +
      (followUp.suggestedTiming ? ` Timing: ${followUp.suggestedTiming}.` : ''),
      { context: { surface: 'session', sessionId: session?.id, projectId: project?.id } }
    );
    setToast(describeInkyOutcome(outcome));
  }, [followUp, meetingName, session?.id, project?.id]);

  const handleCopy = useCallback(() => {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(followUpText(followUp, meetingName))
      .then(() => setToast('Follow-up details copied.'))
      .catch(() => {});
  }, [followUp, meetingName]);

  return (
    <SectionCard
      id={FOLLOW_UP_ANCHOR}
      tabIndex={-1}
      aria-label="We noticed you may need a follow-up"
      sx={{ scrollMarginTop: 96, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}
      title="We noticed you may need a follow-up"
      description={followUp.topic}
      icon={<EventRepeat />}
      actions={
        <>
          <Button size="small" startIcon={<ContentCopy />} onClick={handleCopy}>Copy</Button>
          <Button size="small" variant="contained" startIcon={<AutoAwesome />} onClick={handlePlan}>
            Plan the follow-up
          </Button>
        </>
      }
    >
      <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1 }}>
        {followUp.reason && <Row label="Why">{followUp.reason}</Row>}
        {followUp.suggestedTiming && <Row label="When">{followUp.suggestedTiming}</Row>}
        {followUp.suggestedAttendees.length > 0 && (
          <Row label="Who">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {followUp.suggestedAttendees.map((name) => <Chip key={name} size="small" label={name} />)}
            </Box>
          </Row>
        )}
        {followUp.evidenceQuote && (
          <Row label="From the meeting">
            <Typography component="q" variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              {followUp.evidenceQuote}
            </Typography>
          </Row>
        )}
      </Box>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </SectionCard>
  );
}

/**
 * Label and value row.
 * @param {{label: string, children: React.ReactNode}} props - Props.
 * @returns {JSX.Element} Row.
 */
function Row({ label, children }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '130px minmax(0, 1fr)' }, gap: { xs: 0.25, sm: 1 } }}>
      <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
        {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
      </Box>
    </Box>
  );
}
